import { useState, useEffect, useRef } from 'react';
import { Icon } from '../../icons';
import Avatar from '../ui/Avatar';
import SearchBar from '../ui/SearchBar';
import { getNotifications } from '../../api/notifications';
import { onMessageListener } from '../../utils/firebase';
import { useSettings } from '../../context/SettingsContext';
import { registerBackAction } from '../../utils/backButtonManager';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

import { useAuth } from '../../context/AuthContext';

const translations = { en, hi, gu };

export default function Header({ title, onToggle, onNav, onLogout, onMobileMenuOpen, onRefresh, user, canGoBack = false, onBack }) {
  const { language = 'en' } = useSettings();
  const { switchTenant } = useAuth();
  const displayTitle = translations[language]?.nav?.[title] || title;

  const [q, setQ] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    return registerBackAction(() => {
      setProfileOpen(false);
      return true;
    }, 10);
  }, [profileOpen]);

  useEffect(() => {
    if (!mobileSearchOpen) return;
    return registerBackAction(() => {
      setMobileSearchOpen(false);
      return true;
    }, 10);
  }, [mobileSearchOpen]);

  const userName = user?.name || "User";
  const userEmail = user?.email || "";
  const userRole = user?.primary_role_label || (user?.roles_list?.[0] ? user.roles_list[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Member");

  const userTenants = user?.tenants_list || [];

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const handler = e => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    // Initial fetch
    getNotifications().then(res => {
      if (res.data && res.data.unread_count !== undefined) {
        setUnreadCount(res.data.unread_count);
      }
    }).catch(() => { });

    // Foreground push notifications listener
    onMessageListener().then((payload) => {
      console.log('Received foreground message ', payload);
      setUnreadCount(prev => prev + 1);
      // We could also show a toast here
    }).catch(err => console.log('failed: ', err));

    const onNotificationsRead = (e) => {
      if (e.detail?.count !== undefined) {
        setUnreadCount(e.detail.count);
      }
    };
    window.addEventListener('notifications-read', onNotificationsRead);

    return () => {
      window.removeEventListener('notifications-read', onNotificationsRead);
    };
  }, []);

  return (
    <div
      className="bg-white dark:bg-slate-950 border-b border-border dark:border-slate-800 flex-shrink-0 relative z-20"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Main header row */}
      <div className="h-14 flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-5">
        {/* Hamburger - opens desktop collapse on lg+, mobile drawer on <lg */}
        <button
          onClick={() => {
            if (window.innerWidth >= 1024) onToggle();
            else if (onMobileMenuOpen) onMobileMenuOpen();
          }}
          onTouchStart={() => {}}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-muted dark:text-slate-400 hover:text-ink dark:hover:text-slate-100 transition-all duration-300 active:scale-[0.97] active:opacity-60 active:transition-none bg-transparent border-0 cursor-pointer flex-shrink-0"
        >
          <Icon name="menu" size={16} />
        </button>

        {/* Title */}
        <h1 className="text-sm font-bold text-ink dark:text-slate-100 flex-1 truncate my-0">{displayTitle}</h1>

        {/* Desktop search */}
        <SearchBar
          value={q}
          onChange={setQ}
          placeholder={translations[language]?.header?.search_placeholder || "Search anything…"}
          className="hidden md:flex w-56"
        />

        {/* Mobile search toggle */}
        <button
          onClick={() => setMobileSearchOpen(s => !s)}
          onTouchStart={() => {}}
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-300 active:scale-[0.97] active:opacity-60 active:transition-none bg-transparent border-0 cursor-pointer flex-shrink-0"
        >
          <Icon name="search" size={16} className="text-muted dark:text-slate-400" />
        </button>

        {/* Refresh */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            onTouchStart={() => {}}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-300 active:scale-[0.97] active:opacity-60 active:transition-none bg-transparent border-0 cursor-pointer flex-shrink-0"
            title={translations[language]?.header?.reload_page || "Reload current page"}
          >
            <Icon name="refresh" size={16} className="text-muted dark:text-slate-400" />
          </button>
        )}

        {/* Notifications */}
        <button
          onTouchStart={() => {}}
          className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-300 active:scale-[0.97] active:opacity-60 active:transition-none bg-transparent border-0 cursor-pointer flex-shrink-0"
          onClick={() => onNav("notifications")}
        >
          <Icon name="notifications" size={16} className="text-muted dark:text-slate-400" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Profile dropdown */}
        <div className="relative flex-shrink-0" ref={profileRef}>
          <button
            onTouchStart={() => {}}
            onClick={() => setProfileOpen(o => !o)}
            className="flex items-center gap-2 px-1.5 sm:px-2 py-1.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-300 active:scale-[0.97] active:opacity-60 active:transition-none border-0 bg-transparent cursor-pointer min-h-[44px]"
          >
            <Avatar name={userName} size={30} />
            <div className="text-left hidden sm:block">
              <div className="text-xs font-semibold text-ink dark:text-slate-200">{userName}</div>
              <div className="text-sm text-muted dark:text-slate-400">{userRole}</div>
            </div>
            <Icon name="chevron_down" size={12} className="text-muted dark:text-slate-400 hidden sm:block" />
          </button>

          {profileOpen && (
            <div className="dropdown-menu right-0 w-64 !overflow-y-auto" style={{ maxHeight: '80vh' }}>
              <div className="px-4 py-3 border-b border-border dark:border-slate-700">
                <div className="text-sm font-semibold text-ink dark:text-slate-100">{userName}</div>
                <div className="text-xs text-muted dark:text-slate-400">{userEmail}</div>
              </div>

              {userTenants.length > 0 && (
                <div className="border-b border-border dark:border-slate-700 py-1">
                  <div className="px-4 py-1.5 text-sm font-bold text-muted dark:text-slate-400 uppercase tracking-wider">
                    Switch Society
                  </div>
                  {userTenants.map(t => {
                    // Always use loose equality for tenant ID comparison as localStorage stores strings
                    const isActive = String(t.id) === String(localStorage.getItem('tenant_id') || user?.tenant?.id);

                    return (
                      <button
                        key={t.id}
                        disabled={isActive}
                        onClick={() => {
                          if (!isActive) {
                            setProfileOpen(false);
                            switchTenant(t.id);
                          }
                        }}
                        className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors border-0 text-left ${isActive
                          ? 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-primary-dark/30 cursor-default font-semibold'
                          : 'bg-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer'
                          }`}
                      >
                        <span className="truncate flex-1 font-medium">{t.name || t.slug || t.id}</span>
                        {isActive && <Icon name="check" size={16} className="ml-2 flex-shrink-0" />}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => { onNav("create-society"); setProfileOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-primary dark:text-blue-400 font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-0 bg-transparent cursor-pointer text-left mt-1"
                  >
                    <Icon name="plus" size={14} className="text-primary dark:text-blue-400" />
                    Add Society
                  </button>
                </div>
              )}

              {[
                { icon: "user", label: "My Profile", id: "my-profile" },
                { icon: "settings", label: "Settings", id: "settings" },
                { icon: "building", label: "Society Settings", id: "society-settings" },
              ].map(item => (
                <button
                  key={item.label}
                  onClick={() => { onNav(item.id); setProfileOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors bg-transparent border-0 cursor-pointer text-left min-h-[44px]"
                >
                  <Icon name={item.icon} size={14} className="text-muted dark:text-slate-400" />
                  {translations[language]?.nav?.[item.label] || item.label}
                </button>
              ))}
              <button
                onClick={() => { onNav("fire-alert"); setProfileOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors bg-transparent border-0 cursor-pointer text-left border-t border-border dark:border-slate-700 mt-1 pt-2.5 min-h-[44px]"
              >
                <Icon name="fire" size={14} color="#ef4444" />
                🔥 {translations[language]?.nav?.["Emergency Alert System"] || "Emergency Alert"}
              </button>
              <div className="border-t border-border dark:border-slate-700 mt-1 pt-1">
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors bg-transparent border-0 cursor-pointer text-left min-h-[44px]"
                >
                  <Icon name="logout" size={14} className="text-red-500 dark:text-red-400" />
                  {translations[language]?.header?.sign_out || "Sign Out"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile expandable search row */}
      {mobileSearchOpen && (
        <div className="md:hidden px-3 pb-3">
          <SearchBar
            value={q}
            onChange={setQ}
            placeholder={translations[language]?.header?.search_placeholder || "Search anything…"}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}
